/**
 * WhatsNewDialog — shows a single tip at a time. No prev/next/page navigation:
 * dismiss / remind-later / try-now each act on the current tip and the caller
 * advances to the next one (if any) behind the scenes.
 *
 * When the tip carries a registered `experienceKey`, its component renders inside
 * the modal shell (which still owns chrome, impressions, snooze); otherwise the
 * default card is shown — see WhatsNewCard, which the admin preview shares.
 *
 * The default card comes in two presentations: the footer pair (Dismiss / Remind
 * me later) or `close-only`, which drops the footer and leaves the top-right ✕ as
 * the single control. An experience picks its own chrome via `ownsControls`, so
 * `presentation` does not apply to it.
 */

import { Suspense, useEffect } from 'react';
import { Modal } from '../components/Modal';
import type { WhatsNewTip } from './whatsNewApi';
import { WhatsNewCard } from './WhatsNewCard';
import { getExperience, type WhatsNewExperienceProps } from './experienceRegistry';
import './WhatsNewDialog.css';

// Subject (from the tip's "Subject solved <=" condition) -> subject id, used to build
// that subject's practice homepage relative to the current origin (host/port may vary
// per environment, so we never hardcode a full URL).
const SUBJECT_IDS: Record<string, string> = {
    maths: 'cc34a211-7aae-415d-aca1-215abd8f105e',
    chemistry: '513a3e89-b17f-485f-a81e-0e980253bd55',
    physics: '72eba892-ca2c-40f3-b8c7-27f0a0bbf185',
};

interface WhatsNewDialogProps {
    isOpen: boolean;
    onClose: () => void;
    currentTip: WhatsNewTip | null;
    onDismiss: () => void;
    onRemindLater: () => void;
    /** Dismisses the current tip, then navigates to the given URL. */
    onTryNow: (url: string) => void;
    /** Terminal success for a custom experience (dismiss cooldown, tracked as 'completed'). */
    onComplete: (meta?: unknown) => void;
    /** Submit a survey response (persist answers + mark completed) and close. */
    onSubmit: (answers: Record<string, unknown>) => void;
    /** The experience has nothing to show — step aside without spending the
     *  day's cooldown on a card the user never saw. */
    onSkip: () => void;
    /** Beacon one impression + start the dwell clock. Called when the tip's
     *  content is genuinely on screen — see the effect below. */
    onShown: () => void;
    /** In-experience analytics event without closing. */
    onAction: (key: string, meta?: unknown) => void;
}

export function WhatsNewDialog({
    isOpen,
    onClose,
    currentTip,
    onDismiss,
    onRemindLater,
    onTryNow,
    onComplete,
    onSubmit,
    onSkip,
    onShown,
    onAction,
}: WhatsNewDialogProps) {
    const experience = getExperience(currentTip?.experienceKey);

    // A default card is its own content: on screen the moment it renders, so the
    // impression fires here. An experience that declares `defersImpression`
    // still has to resolve data before it knows whether it has anything worth
    // showing, so it owns the call via `shown()` — counting it here would credit
    // an impression to a card that goes on to skip.
    const deferImpression = !!experience?.defersImpression;
    useEffect(() => {
        if (isOpen && currentTip && !deferImpression) onShown();
    }, [isOpen, currentTip, deferImpression, onShown]);

    if (!currentTip) return null;

    // ── Custom experience ────────────────────────────────────────────
    if (experience) {
        const ExperienceComponent = experience.component;
        const props: WhatsNewExperienceProps = {
            tip: currentTip,
            data: currentTip.experienceData,
            onDismiss,
            onRemindLater,
            onComplete,
            submit: onSubmit,
            onAction,
            close: onClose,
            skip: onSkip,
            shown: onShown,
        };
        return (
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={experience.title ?? "What's New"}
                size="md"
                closeOnOverlayClick={true}
                closeOnEscape={true}
                footer={experience.ownsControls ? undefined : (
                    <div className="wn-footer">
                        <div className="wn-action-group">
                            <button className="wn-nav-btn" onClick={onDismiss}>Dismiss</button>
                            <button className="wn-nav-btn wn-nav-remind" onClick={onRemindLater}>Remind me later</button>
                        </div>
                    </div>
                )}
            >
                {/* Apps may register a lazily loaded component, so the
                    experience's code arrives only with a tip that needs it. */}
                <Suspense fallback={null}>
                    <ExperienceComponent {...props} />
                </Suspense>
            </Modal>
        );
    }

    // ── Default card ─────────────────────────────────────────────────
    const subjectId = currentTip.condition ? SUBJECT_IDS[currentTip.condition.excludeSubject] : undefined;
    const tryNowUrl = subjectId ? `${window.location.origin}/subject/${subjectId}` : undefined;

    // 'close-only': the same card with no footer. The ✕ the shell already draws
    // top-right — the header's, or the floating one on a headless card — becomes
    // the single control, so it has to carry the weight the Dismiss button did:
    // it snoozes on `afterDismissDays`, not the softer remind-later delay an
    // implicit close normally gets. With one way out, every way out means the
    // same thing, so Escape and the overlay follow it rather than staying softer
    // than the only button on screen. A Try Now link has no footer to live in,
    // which is the trade this presentation makes.
    const closeOnly = currentTip.presentation === 'close-only';

    return (
        <WhatsNewCard
            isOpen={isOpen}
            onClose={closeOnly ? onDismiss : onClose}
            title={currentTip.title}
            body={currentTip.body}
            imageUrl={currentTip.imageUrl}
            imageFit={currentTip.imageFit}
            footer={closeOnly ? undefined : (
                <div className="wn-footer">
                    <div className="wn-action-group">
                        <button className="wn-nav-btn" onClick={onDismiss}>
                            Dismiss
                        </button>
                        <button className="wn-nav-btn wn-nav-remind" onClick={onRemindLater}>
                            Remind me later
                        </button>
                        {tryNowUrl && (
                            <button
                                className="wn-nav-btn wn-nav-try"
                                onClick={() => onTryNow(tryNowUrl)}
                            >
                                Try Now
                            </button>
                        )}
                    </div>
                </div>
            )}
        />
    );
}
